// ======================== NETWORK CONTEXT ========================
// Gestion de l'état réseau + PWA install + Notifications

import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { offlineQueue, ONLINE_ONLY_ACTIONS } from '../utils/offlineQueue';

const NetworkContext = createContext(null);

export const useNetwork = () => {
  const context = useContext(NetworkContext);
  if (!context) {
    throw new Error('useNetwork must be used within NetworkProvider');
  }
  return context;
};

export const NetworkProvider = ({ children }) => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [syncInProgress, setSyncInProgress] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const [showOfflineBanner, setShowOfflineBanner] = useState(false);
  
  // PWA Install
  const [installPrompt, setInstallPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isInstallable, setIsInstallable] = useState(false);
  
  // Push Notifications
  const [notificationPermission, setNotificationPermission] = useState(
    typeof Notification !== 'undefined' ? Notification.permission : 'default'
  );
  
  // ======================== NETWORK STATUS ========================
  
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      setShowOfflineBanner(false);
    };
    
    const handleOffline = () => {
      setIsOnline(false);
      setShowOfflineBanner(true);
    };
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    // Subscribe to offline queue changes (if available)
    let unsubscribe = () => {};
    if (offlineQueue && typeof offlineQueue.subscribe === 'function') {
      unsubscribe = offlineQueue.subscribe((status) => {
        setIsOnline(status.isOnline);
        setSyncInProgress(status.syncInProgress);
        setShowOfflineBanner(!status.isOnline);
      });
    }
    
    // Periodic ping to verify connection
    const pingInterval = setInterval(async () => {
      try {
        const response = await fetch('/api/health', { 
          method: 'HEAD',
          cache: 'no-cache'
        });
        if (!response.ok) throw new Error('Ping failed');
        if (!isOnline) {
          setIsOnline(true);
          setShowOfflineBanner(false);
        }
      } catch (e) {
        if (isOnline) {
          setIsOnline(false);
          setShowOfflineBanner(true);
        }
      }
    }, 30000);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      if (unsubscribe) unsubscribe();
      clearInterval(pingInterval);
    };
  }, [isOnline]);
  
  // Update pending count
  useEffect(() => {
    const updatePendingCount = async () => {
      try {
        const items = await offlineQueue.getPendingItems();
        setPendingCount(items.length);
      } catch (e) {
        console.error('Error getting pending count:', e);
      }
    };
    
    updatePendingCount();
    const interval = setInterval(updatePendingCount, 5000);
    
    return () => clearInterval(interval);
  }, [syncInProgress]);
  
  // ======================== PWA INSTALL ========================
  
  useEffect(() => {
    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
    }
    
    // Listen for install prompt
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
      setIsInstallable(true);
    };
    
    const handleAppInstalled = () => {
      setIsInstalled(true);
      setIsInstallable(false);
      setInstallPrompt(null);
    };
    
    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    window.addEventListener('appinstalled', handleAppInstalled);
    
    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);
  
  const promptInstall = useCallback(async () => {
    if (!installPrompt) return false;
    
    try {
      installPrompt.prompt();
      const { outcome } = await installPrompt.userChoice;
      
      if (outcome === 'accepted') {
        setIsInstalled(true);
        setIsInstallable(false);
      }
      
      setInstallPrompt(null);
      return outcome === 'accepted';
    } catch (e) {
      console.error('Install error:', e);
      return false;
    }
  }, [installPrompt]);
  
  // ======================== NOTIFICATIONS ========================
  
  const requestNotificationPermission = useCallback(async () => {
    if (typeof Notification === 'undefined') {
      return 'unsupported';
    }
    
    if (Notification.permission === 'granted') {
      setNotificationPermission('granted');
      return 'granted';
    }
    
    try {
      const permission = await Notification.requestPermission();
      setNotificationPermission(permission);
      return permission;
    } catch (e) {
      console.error('Notification permission error:', e);
      return 'denied';
    }
  }, []);
  
  const subscribeToPush = useCallback(async () => {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      return null;
    }
    
    try {
      const registration = await navigator.serviceWorker.ready;
      
      // Check existing subscription
      let subscription = await registration.pushManager.getSubscription();
      
      if (!subscription) {
        // Create new subscription (would need VAPID key in production)
        // For now, just return null - push server not configured
        console.log('Push subscription not available (needs VAPID key)');
        return null;
      }
      
      return subscription;
    } catch (e) {
      console.error('Push subscription error:', e);
      return null;
    }
  }, []);
  
  // ======================== OFFLINE ACTION CHECK ========================
  
  const canPerformAction = useCallback((actionType) => {
    if (isOnline) return true;
    return !ONLINE_ONLY_ACTIONS.includes(actionType);
  }, [isOnline]);
  
  const getOfflineMessage = useCallback((actionType) => {
    const messages = {
      VALIDATE_SALES: 'Connexion requise : la validation des ventes doit se faire en ligne.',
      CREATE_USER: 'Connexion requise : la gestion des utilisateurs doit se faire en ligne.',
      UPDATE_USER_ROLE: 'Connexion requise : la modification des rôles doit se faire en ligne.',
      DELETE_USER: 'Connexion requise : la suppression d\'utilisateurs doit se faire en ligne.',
      CREATE_EXPENSE: 'Connexion requise : les charges se gèrent en ligne.',
      UPDATE_EXPENSE: 'Connexion requise : les charges se gèrent en ligne.',
      DELETE_EXPENSE: 'Connexion requise : les charges se gèrent en ligne.',
      IBAKA_ALERT: 'Urgence : pas de réseau. Appelez immédiatement le Directeur.'
    };
    
    return messages[actionType] || 'Cette action nécessite une connexion internet.';
  }, []);
  
  // ======================== QUEUE OPERATIONS ========================
  
  const addToOfflineQueue = useCallback(async (action) => {
    return await offlineQueue.addToQueue(action);
  }, []);
  
  const syncOfflineQueue = useCallback(async () => {
    return await offlineQueue.syncQueue();
  }, []);
  
  // ======================== CONTEXT VALUE ========================
  
  const value = {
    // Network status
    isOnline,
    syncInProgress,
    pendingCount,
    showOfflineBanner,
    setShowOfflineBanner,
    
    // PWA Install
    isInstalled,
    isInstallable,
    promptInstall,
    
    // Notifications
    notificationPermission,
    requestNotificationPermission,
    subscribeToPush,
    
    // Offline actions
    canPerformAction,
    getOfflineMessage,
    addToOfflineQueue,
    syncOfflineQueue
  };
  
  return (
    <NetworkContext.Provider value={value}>
      {children}
    </NetworkContext.Provider>
  );
};

export default NetworkContext;
